import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import CustomLines from "../home/customLines";
import CustomBigText from "../sharedComponents/customBigText";
import CustomBlockCard from "./customBlockCards";
import ImageBlockOne from "../../assets/images/blockOne_1.png";
import ImageBlockTwo from "../../assets/images/blockTwo_1.png";
import ImageBlockedThree from "../../assets/images/blockThree_1.png";
import ImageBlockedFour from "../../assets/images/blockFour_1.png";
import ImageBlockedFive from "../../assets/images/blockFive_1.png";
import ImageBlockedSix from "../../assets/images/blockSix_1.png";
import { useTranslation } from "react-i18next";
import {
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import useLanguageDirection from "../../hooks/useLanguageDirection";

// Custom Arrow Components with RTL support
const CustomPrevArrow = ({ onClick, direction }) => {
  const Icon =
    direction === "rtl" ? ChevronDoubleRightIcon : ChevronDoubleLeftIcon;
  const position = direction === "rtl" ? "right-[0px]" : "left-[0px]";

  return (
    <button
      onClick={onClick}
      className={`absolute z-10 p-2 -translate-y-1/2 bg-white duration-300 hover:bg-custom-yellow top-1/2 rounded-full shadow-md ${position} group hidden md:block`}
    >
      <Icon className="text-black w-7 h-7 group-hover:text-white" />
    </button>
  );
};

const CustomNextArrow = ({ onClick, direction }) => {
  const Icon =
    direction === "rtl" ? ChevronDoubleLeftIcon : ChevronDoubleRightIcon;
  const position = direction === "rtl" ? "left-[0px]" : "right-[0px]";

  return (
    <button
      onClick={onClick}
      className={`absolute z-10 p-2 -translate-y-1/2 bg-white duration-300 hover:bg-custom-yellow top-1/2 rounded-full shadow-md ${position} group hidden md:block`}
    >
      <Icon className="text-black w-7 h-7 group-hover:text-white" />
    </button>
  );
};

const BlockedItemsSlider = ({ header }) => {
  const { t } = useTranslation();
  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);
  const [slidesToShow, setSlidesToShow] = useState(4);
  const direction = useLanguageDirection();
  const delayTimeoutRef = useRef(null);
  const blockedItems = [
    { image: ImageBlockedSix, title: t("blockedItems.allLiquids") },
    { image: ImageBlockedThree, title: t("blockedItems.batteries") },
    { image: ImageBlockedFour, title: t("blockedItems.fakeBrands") },
    { image: ImageBlockOne, title: t("blockedItems.medicalSupplies") },
    { image: ImageBlockTwo, title: t("blockedItems.preciousMaterials") },
    { image: ImageBlockedFive, title: t("blockedItems.weaponsAndExplosives") },
  ];

  useEffect(() => {
    const updateSlidesToShow = () => {
      const width = window.innerWidth;
      if (width < 640) setSlidesToShow(2);
      else if (width < 768) setSlidesToShow(3);
      else if (width < 1024) setSlidesToShow(4);
      else setSlidesToShow(5);
    };
    updateSlidesToShow();
    window.addEventListener("resize", updateSlidesToShow);
    return () => window.removeEventListener("resize", updateSlidesToShow);
  }, []);

  const startCustomAutoplay = () => {
    autoPlayRef.current = setInterval(() => {
      if (sliderRef.current) {
        if (direction === "rtl") {
          sliderRef.current.slickPrev();
        } else {
          sliderRef.current.slickNext();
        }
      }
    }, 3000);
  };

  const stopCustomAutoplay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = null;
  };

  const resumeWithDelay = () => {
    if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current);
    delayTimeoutRef.current = setTimeout(() => {
      startCustomAutoplay();
    }, 3000); 
  };

  useEffect(() => {
    startCustomAutoplay();
    return () => {
      stopCustomAutoplay();
      if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current);
    };
  }, [direction]);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow,
    slidesToScroll: 2,
    autoplay: false, 
    rtl: direction === "rtl",
    arrows: true,
    prevArrow: <CustomPrevArrow direction={direction} />,
    nextArrow: <CustomNextArrow direction={direction} />,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 5, arrows: true } },
      { breakpoint: 768, settings: { slidesToShow: 4, arrows: true } },
      { breakpoint: 640, settings: { slidesToShow: 3, arrows: false } },
      { breakpoint: 480, settings: { slidesToShow: 2, arrows: false } },
    ],
  };

  return (
    <div
      dir={direction}
      onMouseEnter={stopCustomAutoplay}
      onMouseLeave={resumeWithDelay}
      onFocus={stopCustomAutoplay}
      onBlur={resumeWithDelay}
      onTouchStart={stopCustomAutoplay}
      onTouchEnd={resumeWithDelay}
      onTouchCancel={resumeWithDelay}
    >
      <div className="flex flex-col items-center justify-center text-center max-container">
        {header ? (
          <>
            <CustomLines />
            <CustomBigText title={t("customBigText.prohibitedItems")} />
          </>
        ) : (
          <div
            className={`flex w-full ${
              direction === "rtl" ? "justify-end" : "justify-start"
            }`}
          >
            <div className="flex items-center gap-2">
              <NoSymbolIcon className="w-3 h-3 text-black md:w-6 md:h-6" />
              <h2 className="text-xl font-medium text-black md:text-3xl">
                {t("ProhibitedItemsTitle")}
              </h2>
            </div>
          </div>
        )}

        <div className="relative w-full mb-5 md:mb-10 lg:mt-10">
          <div className="px-2 md:px-10">
            <Slider ref={sliderRef} {...sliderSettings}>
              {blockedItems.map((item, index) => (
                <div key={index} className="px-1">
                  <div className="flex-shrink-0 min-w-[150px] max-w-[150px] lg:min-w-[175px] lg:max-w-[175px] mx-auto">
                    <CustomBlockCard image={item.image} title={item.title} />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockedItemsSlider;
